import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import isNil from 'lodash/isNil';

import { injector } from 'Injector';
import { STUDENT_SERVICE, StudentService } from 'student/service';
import { Student, StudentsList, StudentLevel } from 'user/student/Student';
import { AssignmentList, Assignment, Grade, Subject } from 'assignment/Assignment';
import { TeachingPath, TeachingPathsList } from 'teachingPath/TeachingPath';
import { USER_SERVICE, UserService } from 'user/UserService';

import { debounce } from 'utils/debounce';
import { DEBOUNCE_TIME } from 'utils/constants';
import { Locales, StoreState } from 'utils/enums';
import { reportFailure } from 'utils/reportFailure';

const ASSIGNMENTS_PER_PAGE = 10;
const TP_PER_PAGE = 10;
const STUDENTS_PER_PAGE = 8;

export class StudentsListStore {

  private userService: UserService = injector.get(USER_SERVICE);
  private studentService: StudentService = injector.get(STUDENT_SERVICE);
  private studentsListService = new StudentsList();
  private assignmentsListService: AssignmentList = new AssignmentList();
  private TeachingPathsListService: TeachingPathsList = new TeachingPathsList();

  @observable public studentsListState: StoreState = StoreState.PENDING;
  @observable public studentsList: Array<Student> = [];
  @observable public studentsListForSkeleton: Array<Student> = new Array<Student>(STUDENTS_PER_PAGE).fill(new Student({ id: 0, name: '', photo: '' , schools: [] }));
  @observable public totalPages: number = 0;
  @observable public currentStudent: Student | null = null;

  @observable public assignmentsListState: StoreState = StoreState.PENDING;
  @observable public teachingPathListState: StoreState = StoreState.PENDING;
  @observable public assignmentsList: Array<Assignment> = [];
  @observable public teachingpathList: Array<TeachingPath> = [];
  @observable public assignmentsListForSkeleton: Array<Assignment> = new Array<Assignment>(ASSIGNMENTS_PER_PAGE).fill(new Assignment({ id: 0 }));
  @observable public teachingpathListForSkeleton: Array<TeachingPath> = new Array<TeachingPath>(TP_PER_PAGE).fill(new TeachingPath({ id: 0, title: '' }));
  @observable public paginationTotalPages: number = 0;

  @observable public gradesList: Array<Grade> = [];
  @observable public subjectsList: Array<Subject> = [];
  @observable public levelsList: Array<StudentLevel> = [];

  // Through an arrow, so `this` is the store however the wrapper is called, and the method is
  // read when it runs, after makeObservable has wrapped it as an action.
  public studentsDebounceWrapper = debounce(() => { void this.getStudentsList(); }, DEBOUNCE_TIME);
  public assignmentsDebounceWrapper = debounce(() => { void this.getAssignmentsList(); }, DEBOUNCE_TIME);
  public teachingPathDebounceWrapper = debounce(() => { void this.getTeachingPathList(); }, DEBOUNCE_TIME);

  constructor() {
    makeObservable(this);
  }

  @computed
  public get filter() {
    return this.studentsListService.filter;
  }

  @computed
  public get currentAssignmentListPage(): number {
    return !isNil(this.assignmentsListService.filter.page) ? this.assignmentsListService.filter.page : 1;
  }

  @computed
  public get currentTeachingpathListPage(): number {
    return !isNil(this.TeachingPathsListService.filter.page) ? this.TeachingPathsListService.filter.page : 1;
  }

  @action
  public async getStudentsList() {
    this.studentsListState = StoreState.LOADING;
    try {
      const response = await this.studentsListService.getStudentsList();

      runInAction(() => {
        this.studentsList = response.students;
        this.totalPages = response.total_pages;
        this.studentsListState = StoreState.PENDING;
      });
    } catch (error) {
      runInAction(() => { this.studentsListState = StoreState.ERROR; });
      reportFailure(error);
    }
  }

  public setFiltersPage(number: number) {
    this.studentsListService.setFiltersPage(number);
    this.getStudentsList();
  }

  public setFiltersPerPage(number: number) {
    this.studentsListService.setFiltersPerPage(number);
    this.getStudentsList();
  }

  public setFiltersGrade(grade: number | null) {
    this.studentsListService.setFiltersGrade(grade);
    this.studentsListService.setFiltersPage(1);
    this.getStudentsList();
  }

  public setFiltersSubject(subject: number | null) {
    this.studentsListService.setFiltersSubject(subject);
    this.studentsListService.setFiltersPage(1);
    this.getStudentsList();
  }

  public setFiltersSearchQuery(searchQuery: string) {
    this.studentsListService.setFiltersSearchQuery(searchQuery);
    this.studentsListService.setFiltersPage(1);
    this.studentsDebounceWrapper();
  }

  /** Fired from the level picker; a failure used to go untold (#217). */
  public async changeStudentLevel(studentId: number, levelId: number) {
    try {
      await this.studentService.changeStudentLevel(studentId, levelId);
    } catch (error) {
      reportFailure(error);
    }
  }

  /**
   * The student whose overview panel is open, or `null` for none.
   *
   * `StudentsList` assigned this directly on click and on close. Both are observed — the list
   * highlights the current row and the overview renders from it — and neither was in an
   * action. See docs/mobx-6-class-d.md.
   */
  @action
  public setCurrentStudent(student: Student | null) {
    this.currentStudent = student;
  }

  @action
  public clearStudentsList() {
    this.studentsList = [];
  }

  @action
  public clearAssignmentsList() {
    this.assignmentsList = [];
  }

  @action
  public clearTeachingPathList() {
    this.teachingpathList = [];
  }

  @action
  public async getAssignmentsList() {
    this.assignmentsListState = StoreState.LOADING;
    try {
      this.TeachingPathsListService.setFiltersPerPage(ASSIGNMENTS_PER_PAGE);
      const response = await this.assignmentsListService.getAssignmentListOfStudentInList(this.currentStudent!.id);

      runInAction(() => {
        this.assignmentsList = response.myAssignments;
        this.paginationTotalPages = response.total_pages;
        this.assignmentsListState = StoreState.PENDING;
      });
    } catch (error) {
      runInAction(() => { this.assignmentsListState = StoreState.ERROR; });
      reportFailure(error);
    }
  }

  @action
  public async getTeachingPathList() {
    this.teachingPathListState = StoreState.LOADING;
    try {
      this.assignmentsListService.setFiltersPerPage(ASSIGNMENTS_PER_PAGE);
      const response = await this.TeachingPathsListService.getTeachingPathListOfStudentInList(this.currentStudent!.id);

      runInAction(() => {
        this.teachingpathList = response.teachingPathsList;
        this.paginationTotalPages = response.total_pages;
        this.teachingPathListState = StoreState.PENDING;
      });
    } catch (error) {
      runInAction(() => { this.teachingPathListState = StoreState.ERROR; });
      reportFailure(error);
    }
  }

  public setFiltersIsEvaluated(isEvaluated: string | null) {
    this.assignmentsListService.setFiltersIsEvaluated(isEvaluated);
    this.assignmentsListService.setFiltersPage(1);
    this.getAssignmentsList();
  }

  public setFiltersIsAnswered(status: string | null) {
    this.assignmentsListService.setFiltersIsAnswered(status);
    this.assignmentsListService.setFiltersPage(1);
    this.getAssignmentsList();
  }

  public setFiltersIsAnsweredTP(status: string | null) {
    this.TeachingPathsListService.setFiltersIsAnswered(status);
    this.TeachingPathsListService.setFiltersPage(1);
    this.getTeachingPathList();
  }

  public setFiltersIsEvaluatedTP(isEvaluated: string | null) {
    this.TeachingPathsListService.setFiltersIsEvaluated(isEvaluated);
    this.TeachingPathsListService.setFiltersPage(1);
    this.getAssignmentsList();
  }

  public async setAssignmentsFiltersPage(number: number) {
    if (this.paginationTotalPages === this.currentAssignmentListPage) {
      return null;
    }
    this.assignmentsListService.setFiltersPage(number);
    this.assignmentsListState = StoreState.LOADING;
    try {
      this.assignmentsListService.setFiltersPerPage(ASSIGNMENTS_PER_PAGE);
      const response = await this.assignmentsListService.getAssignmentListOfStudentInList(this.currentStudent!.id);

      this.assignmentsList = this.assignmentsList.concat(response.myAssignments);
      this.paginationTotalPages = response.total_pages;
      this.assignmentsListState = StoreState.PENDING;
    } catch (error) {
      runInAction(() => { this.assignmentsListState = StoreState.ERROR; });
      reportFailure(error);
    }
  }

  public async setTeachingPathFiltersPage(number: number) {
    if (this.paginationTotalPages === this.currentTeachingpathListPage) {
      return null;
    }
    this.TeachingPathsListService.setFiltersPage(number);
    this.teachingPathListState = StoreState.LOADING;
    try {
      this.TeachingPathsListService.setFiltersPerPage(ASSIGNMENTS_PER_PAGE);
      const response = await this.TeachingPathsListService.getTeachingPathListOfStudentInList(this.currentStudent!.id);

      this.teachingpathList = this.teachingpathList.concat(response.teachingPathsList);
      this.paginationTotalPages = response.total_pages;
      this.teachingPathListState = StoreState.PENDING;
    } catch (error) {
      runInAction(() => { this.teachingPathListState = StoreState.ERROR; });
      reportFailure(error);
    }
  }

  public setAssignmentsFiltersSearchQuery(searchQuery: string) {
    this.assignmentsListService.setFiltersSearchQuery(searchQuery);
    this.assignmentsListService.setFiltersPage(1);
    this.assignmentsDebounceWrapper();
  }

  public setTeachingPathFiltersSearchQuery(searchQuery: string) {
    this.TeachingPathsListService.setFiltersSearchQuery(searchQuery);
    this.TeachingPathsListService.setFiltersPage(1);
    this.assignmentsDebounceWrapper();
  }

  public setAssignmentsFilterSorting(orderField: string, order: string) {
    this.assignmentsListService.setFiltersSorting(orderField, order);
    this.assignmentsListService.setFiltersPage(1);
    this.getAssignmentsList();
  }

  public setTeachingPathFilterSorting(orderField: string, order: string) {
    this.TeachingPathsListService.setFiltersSorting(orderField, order);
    this.TeachingPathsListService.setFiltersPage(1);
    this.getTeachingPathList();
  }

  public async getStudentAdditionalData() {
    const response = await this.studentService.getStudentAdditionalData();
    this.gradesList = response.grades;
    this.subjectsList = response.subjects;
    this.levelsList = response.levels;
  }

  public getCurrentLocale() {
    return this.userService.getCurrentLocale() || Locales.EN;
  }

  public resetFilters() {
    this.assignmentsListService.setFiltersPage(0);
    this.assignmentsListService.setFiltersIsAnswered(null);
    this.assignmentsListService.setFiltersIsEvaluated(null);
    this.assignmentsListService.setFiltersSearchQuery('');
  }

  public resetFiltersTp() {
    this.TeachingPathsListService.setFiltersPage(0);
    this.TeachingPathsListService.setFiltersIsAnswered(null);
    this.TeachingPathsListService.setFiltersIsEvaluated(null);
    this.TeachingPathsListService.setFiltersSearchQuery('');
  }
}
